import { useEffect, useMemo, useState } from 'react';
import { Plus, Wallet, TrendingDown, TrendingUp, Download } from 'lucide-react';
import {
  UniversalLoadingState,
  Card,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  FormField,
  NotificationCard,
  StatusBadge,
  useToast, UniversalPage } from '@afrilink/ui';
import {
  adminFinancialsService,
  type IncomeStatement,
  type ExpenseCategory,
  type ExpenseRow,
  type CurrentMrr,
  EXPENSE_CATEGORY_LABEL,
  MANUAL_EXPENSE_CATEGORIES,
} from '@/domains/financials/services/adminFinancialsService';
import { adminDomainService, type AdminDomainBalance } from '@/domains/domains-registry/services/adminDomainService';
import { exportRowsAsCsv, todayStamp } from '@/shared/utils/csvExport';
import { sastMonthKey } from '@/shared/utils/sastDate';
import { IncomeStatementCard, formatZAR } from './components';

/**
 * The current month, in SAST. (F-07)
 *
 * This was `new Date().getUTCMonth()`, which asks the wrong question: the
 * database runs in UTC and the business runs in SAST (UTC+2, no DST). Between
 * 00:00 and 02:00 SAST on the first of a month it is still the previous month
 * in UTC, so the income statement opened on LAST month's figures with nothing
 * on screen saying so.
 *
 * CLAUDE.md §4.14 rules on exactly this and `sastMonthKey()` was written for
 * it — the helper has existed since the SAST ruling and had zero imports
 * anywhere in the admin console until now.
 *
 * `sastMonthKey()` returns `YYYY-MM-01`; this page's state is bound to an
 * `<input type="month">`, which wants `YYYY-MM`.
 */
function todayMonth(): string {
  return sastMonthKey().slice(0, 7);
}

/**
 * Step back one month.
 *
 * DELIBERATELY still `Date.UTC` and NOT a SAST helper. This is pure calendar
 * arithmetic on a year and month that have already been resolved — no "now",
 * no instant, nothing a timezone can shift. `Date.UTC(y, m - 2, 1)` is exact
 * and handles the January -> December rollover. Converting it to a SAST helper
 * would add a timezone to a calculation that has none, which is how a correct
 * function becomes a subtle one.
 */
function prevMonth(yearMonth: string): string {
  const parts = yearMonth.split('-').map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = new Date(Date.UTC(y, m - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function MonthlyStatementPageBody() {
  const { toast } = useToast();
  const [yearMonth, setYearMonth] = useState(todayMonth());
  const [statement, setStatement] = useState<IncomeStatement | null>(null);
  const [prev, setPrev] = useState<IncomeStatement | null>(null);
  const [expenses, setExpenses] = useState<ExpenseRow[]>([]);
  const [balance, setBalance] = useState<AdminDomainBalance | null>(null);
  const [mrr, setMrr] = useState<CurrentMrr | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  function reload() {
    setError(null);
    Promise.all([
      adminFinancialsService.getMonthlyStatement(yearMonth),
      adminFinancialsService.getMonthlyStatement(prevMonth(yearMonth)),
      adminFinancialsService.listExpenses(yearMonth),
      adminDomainService.getBalance().catch(() => null),
      adminFinancialsService.getCurrentMrr().catch(() => null),
    ])
      .then(([s, p, e, b, m]) => {
        setStatement(s);
        setPrev(p);
        setExpenses(e);
        setBalance(b);
        setMrr(m);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }

  // Deliberate: this effect is meant to run on the listed deps only. (Was an
  // eslint-disable for react-hooks/exhaustive-deps; this config does not
  // register that plugin, so the directive was dangling.)
  useEffect(reload, [yearMonth]);

  return (
    <div className="space-y-4">
      <Card interactive={false} className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <FormField label="Month" htmlFor="month-pick">
            <Input
              id="month-pick"
              type="month"
              value={yearMonth}
              onChange={(e) => setYearMonth(e.target.value || todayMonth())}
              max={todayMonth()}
            />
          </FormField>
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!statement}
              onClick={() => {
                if (!statement) return;
                const rows: Array<[string, string]> = [
                  ['Period', `${statement.period_start} → ${statement.period_end}`],
                  ['', ''],
                  ['REVENUE', ''],
                  ['Hosting', String(statement.revenue.hosting)],
                  ['Domains', String(statement.revenue.domains)],
                  ['Other', String(statement.revenue.other)],
                  ['Total revenue', String(statement.revenue.total)],
                  ['', ''],
                  ['EXPENSES', ''],
                  ['WHM hosting', String(statement.expense.whm_hosting)],
                  ['domains.co.za top-up', String(statement.expense.domains_topup)],
                  ['PayFast fees', String(statement.expense.payfast_fees)],
                  ['Internet', String(statement.expense.internet)],
                  ['Infrastructure', String(statement.expense.infrastructure)],
                  ['Tooling', String(statement.expense.tooling)],
                  ['Salary', String(statement.expense.salary)],
                  ['Marketing', String(statement.expense.marketing)],
                  ['Other', String(statement.expense.other)],
                  ['Total expenses', String(statement.expense.total)],
                  ['', ''],
                  ['Net income', String(statement.net)],
                ];
                exportRowsAsCsv(
                  `income-statement-${yearMonth}-${todayStamp()}.csv`,
                  ['Line', 'Amount (ZAR)'],
                  rows,
                );
              }}
            >
              <Download className="h-4 w-4" /> Export CSV
            </Button>
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="h-4 w-4" /> Record expense
            </Button>
          </div>
        </div>
      </Card>

      {balance && (
        <Card interactive={false} className={`p-3 ${balance.isBelowFloor ? 'border-danger/30 bg-danger/5' : 'border-info/30 bg-info/5'}`}>
          <div className="flex items-start gap-3 text-sm">
            <Wallet className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="flex-1">
              <p className="font-medium text-foreground">domains.co.za reseller balance</p>
              <p className="text-xs text-muted-foreground">
                Current: <strong className="text-foreground">{formatZAR(balance.balance)}</strong> ({balance.accountType})
                · Floor <strong className="text-foreground">{formatZAR(balance.balanceFloor)}</strong>
                {balance.isBelowFloor && (
                  <span className="ml-2 text-danger">— below floor; paid mutations blocked</span>
                )}
                {balance.fromCache ? ' · cached' : ' · live'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Top-ups should be recorded via "Record expense" with category{' '}
                <code className="font-sans">{EXPENSE_CATEGORY_LABEL.domains_topup}</code> so the income statement reflects them.
              </p>
            </div>
          </div>
        </Card>
      )}

      {error && <NotificationCard tone="danger" title="Could not load statement" body={error} />}
      {!statement && !error && <UniversalLoadingState variant="spinner" caption="Loading statement…" />}

      {statement && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card interactive={false} className="p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Actual revenue</p>
              <p className="mt-1 text-2xl font-semibold text-success">{formatZAR(statement.revenue.total)}</p>
              {prev && (
                <p className={`mt-1 inline-flex items-center gap-1 text-xs ${statement.revenue.total >= prev.revenue.total ? 'text-success' : 'text-danger'}`}>
                  {statement.revenue.total >= prev.revenue.total ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  vs {formatZAR(prev.revenue.total)} last month
                </p>
              )}
            </Card>
            <Card interactive={false} className={`p-4 ${mrr ? 'border-primary/30 bg-primary/5' : ''}`}>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Projected (current MRR)</p>
              <p className="mt-1 text-2xl font-semibold text-primary">
                {mrr ? formatZAR(mrr.total_mrr) : '…'}
              </p>
              {mrr && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Hosting {formatZAR(mrr.hosting_mrr)} · Domains {formatZAR(mrr.domains_mrr)}
                </p>
              )}
              {mrr && mrr.total_mrr > 0 && (
                <p className={`mt-1 inline-flex items-center gap-1 text-xs ${statement.revenue.total >= mrr.total_mrr ? 'text-success' : 'text-warning'}`}>
                  Collected: {((statement.revenue.total / mrr.total_mrr) * 100).toFixed(0)}% of expected
                </p>
              )}
            </Card>
            <Card interactive={false} className="p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Expenses</p>
              <p className="mt-1 text-2xl font-semibold text-danger">{formatZAR(statement.expense.total)}</p>
              {prev && (
                <p className="mt-1 text-xs text-muted-foreground">
                  vs {formatZAR(prev.expense.total)} last month
                </p>
              )}
            </Card>
            <Card interactive={false} className="p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Net income</p>
              <p className={`mt-1 text-2xl font-semibold ${statement.net >= 0 ? 'text-success' : 'text-danger'}`}>
                {formatZAR(statement.net)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Margin: {statement.revenue.total > 0 ? `${((statement.net / statement.revenue.total) * 100).toFixed(1)}%` : '—'}
              </p>
              {mrr && (
                <p className={`mt-1 text-xs ${(mrr.total_mrr - statement.expense.total) >= 0 ? 'text-success' : 'text-danger'}`}>
                  Projected net (MRR − exp): {formatZAR(mrr.total_mrr - statement.expense.total)}
                </p>
              )}
            </Card>
          </div>

          {mrr && (
            <Card interactive={false} className="border-primary/20 bg-primary/5 p-4 text-sm">
              <p className="text-foreground">
                <strong>Projected monthly income</strong> based on current MRR:{' '}
                <strong className="text-primary">{formatZAR(mrr.total_mrr)}</strong>
                {' — '}
                <span className="text-muted-foreground">
                  hosting from {mrr.hosting_active_links} active subscription{mrr.hosting_active_links === 1 ? '' : 's'} ({formatZAR(mrr.hosting_mrr)}/mo),
                  domains from {mrr.domains_active_links} auto-renewing domain{mrr.domains_active_links === 1 ? '' : 's'} ({formatZAR(mrr.domains_mrr)}/mo, annual price ÷ 12).
                  This is what we expect to collect every month at the current customer base — actual cash-in shows above.
                </span>
              </p>
            </Card>
          )}

          <IncomeStatementCard statement={statement} comparisonStatement={prev ?? undefined} />

          {expenses.length > 0 && (
            <Card interactive={false}>
              <header className="border-b border-border p-3">
                <h3 className="text-sm font-semibold text-foreground">
                  Expenses recorded in {statement.period_label} ({expenses.length})
                </h3>
              </header>
              <div className="divide-y divide-border">
                {expenses.map((e) => (
                  <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
                    <div className="flex items-center gap-3">
                      <StatusBadge tone="info">{EXPENSE_CATEGORY_LABEL[e.category]}</StatusBadge>
                      <span className="text-foreground">{e.description}</span>
                      {e.notes && <span className="text-xs text-muted-foreground">· {e.notes}</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{e.incurred_on}</span>
                      <span className="text-base font-semibold text-danger tabular-nums">{formatZAR(Number(e.amount))}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {showAdd && (
        <RecordExpenseModal
          defaultIncurredOn={`${yearMonth}-15`}
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            toast({ tone: 'success', title: 'Expense recorded' });
            reload();
          }}
        />
      )}
    </div>
  );
}

function RecordExpenseModal({
  defaultIncurredOn,
  onClose,
  onSaved,
}: {
  defaultIncurredOn: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [category, setCategory] = useState<ExpenseCategory>('domains_topup');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [incurredOn, setIncurredOn] = useState(defaultIncurredOn);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const presetDescriptions: Partial<Record<ExpenseCategory, string>> = useMemo(
    () => ({
      domains_topup: 'domains.co.za prepaid balance top-up',
      whm_hosting: 'WHM hosting (domains.co.za reseller package)',
      internet: 'Internet line',
      infrastructure: 'Supabase + Resend',
    }),
    [],
  );

  async function save() {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast({ tone: 'danger', title: 'Invalid amount' });
      return;
    }
    if (!description.trim()) {
      toast({ tone: 'danger', title: 'Description required' });
      return;
    }
    setSaving(true);
    try {
      await adminFinancialsService.addExpense({
        amount: amt,
        category,
        description: description.trim(),
        incurred_on: incurredOn,
        notes: notes.trim() || undefined,
      });
      onSaved();
    } catch (e) {
      toast({ tone: 'danger', title: 'Save failed', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={true} onOpenChange={(v) => !v && onClose()} title="Record expense" size="wide">
      <ModalBody>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Category" htmlFor="exp-cat">
            <select
              id="exp-cat"
              value={category}
              onChange={(e) => {
                const c = e.target.value as ExpenseCategory;
                setCategory(c);
                if (!description) setDescription(presetDescriptions[c] ?? '');
              }}
              disabled={saving}
              className="h-10 w-full rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {MANUAL_EXPENSE_CATEGORIES.map((c) => (
                <option key={c} value={c}>{EXPENSE_CATEGORY_LABEL[c]}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Amount (ZAR)" htmlFor="exp-amt">
            <Input id="exp-amt" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="Description" htmlFor="exp-desc" helper="Short summary that appears on the income statement.">
            <Input id="exp-desc" value={description} onChange={(e) => setDescription(e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="Incurred on" htmlFor="exp-date" helper="The date the expense applies to (not when you logged it).">
            <Input id="exp-date" type="date" value={incurredOn} onChange={(e) => setIncurredOn(e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="Notes (optional)" htmlFor="exp-notes">
            <Input id="exp-notes" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={saving} />
          </FormField>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={save} loading={saving} disabled={saving}>Save expense</Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * These five pages previously rendered NO page header at all — no title, no
 * breadcrumbs — because FinancialReportsLayout supplied a nav row and nothing
 * else. That was a §4.4 violation hiding inside a §4.1 one, and it is why no
 * financial report had a back link.
 */
export default function MonthlyStatementPage() {
  return (
    <UniversalPage
      title="Monthly statement"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Financials', href: '/admin/financials' },
        { label: 'Monthly statement' },
      ]}
    >
      <MonthlyStatementPageBody />
    </UniversalPage>
  );
}
